/**
 * One sanitizer for every HTML string rendered with dangerouslySetInnerHTML
 * or innerHTML.
 *
 * DOMPurify's default profile parses the string the way the browser would
 * and drops only what can run code: <script>, <iframe>, <object>, <embed>,
 * on* handlers, javascript: URLs. Tables, img, SVG, sup/sub, class, style
 * and data-* survive, and text is never rewritten, so LaTeX ($...$, $$...$$,
 * \(...\), \[...\], \frac, &, {}) reaches MathJax unchanged.
 *
 * Run it on the raw string before MathJax typesets, never on DOM MathJax has
 * already rendered.
 */
import DOMPurify from 'dompurify';

// SVG exhibits reuse shapes via <use href="#id">; DOMPurify drops <use> by
// default, so it is allowed back with in-document references only.
const CONFIG = {
  ADD_TAGS: ['use'],
  ADD_ATTR: ['target'],
};

let hookInstalled = false;
function installHook() {
  if (hookInstalled || typeof DOMPurify.addHook !== 'function') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.nodeName && node.nodeName.toLowerCase() === 'use') {
      ['href', 'xlink:href'].forEach((name) => {
        const value = node.getAttribute(name);
        if (value != null && !value.trim().startsWith('#')) node.removeAttribute(name);
      });
    }
  });
  hookInstalled = true;
}

const CACHE_LIMIT = 500;
const cache = new Map();

export function sanitizeHtml(html) {
  if (html == null) return '';
  const input = String(html);
  if (!input) return '';
  if (!/[<&]/.test(input)) return input;
  if (!DOMPurify.isSupported) return input.replace(/</g, '&lt;');
  const hit = cache.get(input);
  if (hit !== undefined) return hit;
  installHook();
  const out = DOMPurify.sanitize(input, CONFIG);
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
  cache.set(input, out);
  return out;
}

export default sanitizeHtml;
